import {
  inject,
  linkedSignal,
  Injectable,
  signal,
  Signal,
  untracked,
  effect,
  ResourceRef,
  computed,
} from "@angular/core";
import { rxResource, toSignal } from "@angular/core/rxjs-interop";
import {
  filter,
  map,
  EMPTY,
  forkJoin,
  of,
  catchError,
  take,
  tap,
  switchMap,
  startWith,
  pairwise,
  finalize,
  firstValueFrom,
} from "rxjs";
import { CachedProjectService } from "../../../shared/services/cached-project.service";
import { CachedPaletteService } from "../../../projects/services/cached-palette.service";
import { WellLog } from "../../../models/wellLog";
import { JobDetail } from "../../../models/jobDetail";
import { BasicSettingsService } from "./basic-settings.store";
import { ModelViewerSettingsService } from "./model-viewer-settings.service";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { NzMessageService } from "ng-zorro-antd/message";
import { ActivatedRoute, Router } from "@angular/router";
import { allSliceProcessings } from "./slice_processings";
import {
  G_JobHorizon,
  G_ProjectHorizon,
  GeneralizedHorizon,
} from "../../../models/generalizedHorizon";

const DEFAULT_MODEL: string = "Vp";

/**
 * Whether a formation marker should be drawn, given a `markersVisibility` map.
 * The map only holds the markers of the log the settings box last built its
 * form for, so an absent key means "not unticked" - i.e. visible.
 */
export function isMarkerVisible(
  markersVisibility: { [key: string]: boolean },
  name: string,
): boolean {
  return markersVisibility[name] !== false;
}

@Injectable({
  providedIn: "root", // This makes it a Singleton
})
export class JobStateService {
  private cachedProjectService = inject(CachedProjectService);
  private cachedPaletteService = inject(CachedPaletteService);
  private settingsService = inject(BasicSettingsService);
  private modelViewerSettings = inject(ModelViewerSettingsService);
  private nzNotificationService = inject(NzNotificationService);
  private nzMessageService = inject(NzMessageService);
  // job ids with a reparse in flight, so repeated new-iteration checks don't stack requests
  private reparsingJobIds = new Set<string>();

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private readonly urlSeed = this.route.snapshot.queryParams;

  synchroniseSettings = signal<boolean>(false);

  // --- SOURCE ---
  public readonly jobData: Signal<JobDetail | null> = toSignal<JobDetail>(
    this.cachedProjectService.currentJob.pipe(
      filter((job): job is any => !!job),
      startWith(null as unknown as JobDetail),
      pairwise(), // Emits [previousJob, currentJob]
      tap(([prev, current]) => {
        if (prev && prev.id !== current.id) {
          // Check whether prev jobs range was customized.
          // jobData hasn't switched yet, so the variable dx are still the prev job's
          const prevIlMax =
            (prev.x2_is_inline ? prev.modelGrid.nx2 : prev.modelGrid.nx1) *
            untracked(() => this.inlineDx());
          const prevXlMax =
            (prev.x2_is_inline ? prev.modelGrid.nx1 : prev.modelGrid.nx2) *
            untracked(() => this.crosslineDx());
          const prevDepthMax =
            prev.modelGrid.nx3 * untracked(() => this.dx3());
          this.previousJobWasCropped.set(
            this.rangesStart.XL() !== 0 ||
              this.rangesStart.IL() !== 0 ||
              this.rangesStart.depth() !== 0 ||
              this.rangesEnd.XL() !== prevXlMax ||
              this.rangesEnd.IL() !== prevIlMax ||
              this.rangesEnd.depth() !== prevDepthMax,
          );
          this.prevDepthMax.set(prevDepthMax);

          // Save the current signal states under the old ID (before the ranges get reset for the new job)
          this.settingsService.save(prev.id, {
            inline: untracked(() => this.inline()),
            crossline: untracked(() => this.crossline()),
            iteration: untracked(() => this.iteration()),
            depth: untracked(() => this.depth()),
            dxOverride: this.dxOverride(),
            rangesStart: this.rangeValues(this.rangesStart),
            rangesEnd: this.rangeValues(this.rangesEnd),
          });

          // if the modelGrids are different, the resetRanges. otherwise preserve ranges across jobs
          if (
            !this.preserveRanges() &&
            (prev.modelGrid.nx1 !== current.modelGrid.nx1 ||
              prev.modelGrid.nx2 !== current.modelGrid.nx2 ||
              prev.modelGrid.nx3 !== current.modelGrid.nx3 ||
              prev.modelGrid.dx !== current.modelGrid.dx)
          ) {
            this.resetRanges(current);
          }

          this.restoreSavedView(current);
        } else if (!prev) {
          // genuinely the FIRST job (prev is null) — initialise the ranges.
          // We deliberately do NOT reset when prev exists and the id is unchanged. That is the
          // SAME job being re-emitted: editing the job
          this.resetRanges(current);
        }
        // else: same job re-emitted with the same id → preserve all view ranges, do nothing.
      }),
      map(([_, current]) => current),
      switchMap((current) => {
        // gotta get/assign the jobSettings before the job actual changes so that anything using it as an effect/dependency has the correct value.
        this.settingsService.loadActiveJobSettings(current.id);
        return of(current);
      }),
    ),
    { initialValue: null },
  );

  // ranges
  rangesStart = {
    IL: signal<number>(0),
    XL: signal<number>(0),
    depth: signal<number>(0),
  };
  rangesEnd = {
    IL: signal<number>(null),
    XL: signal<number>(null),
    depth: signal<number>(null),
  };
  // Whether the range was changed (cropped) for the job that was just replaced
  previousJobWasCropped = signal<boolean>(false);
  prevDepthMax = signal<number>(null);
  preserveRanges = signal<boolean>(false);

  private rangeValues(ranges: { IL: any; XL: any; depth: any; }) {
    return { IL: ranges.IL(), XL: ranges.XL(), depth: ranges.depth() };
  }

  // --- STATE ---
  public readonly inline = linkedSignal<
    {
      log: WellLog;
      job: JobDetail;
    },
    number
  >({
    source: () => ({ log: this.selectedLog(), job: this.jobData() }),
    computation: (source, previous) => {
      // If this is the FIRST time the signal is calculating (no previous value)
      // AND we have a seed value from the URL, use it.
      if (!previous && "inline" in this.urlSeed) {
        return Number(this.urlSeed["inline"]);
      }

      let { job, log } = source;
      // if log is selected, that is the primary case. that inline/crossline used should be that
      if (log) return job.x2_is_inline ? log.crossline : log.inline;

      // do a check here to ensure that the modelGrid is the same
      // if grid is the same, we just preserve the value
      if (
        previous?.value &&
        previous.source.job.modelGrid.nx1 === source.job.modelGrid.nx1 &&
        previous.source.job.modelGrid.nx2 === source.job.modelGrid.nx2 &&
        previous.source.job.modelGrid.dx === source.job.modelGrid.dx
      ) {
        return previous.value;
      }

      // The below is for if we want to preserve settings of the inline crossline and depth from the cache storage
      // Detect if job change
      const isNewJob = source.job.id !== previous?.source?.job.id; // this might be unnecessary tho cuase we might only get here for cases where
      if (isNewJob) {
        let settings = this.settingsService.jobSettings();
        // let settings = {};
        return (
          settings["inline"] ??
          (job.x2_is_inline
            ? Math.floor(job.modelGrid.nx2 / 2)
            : Math.floor(job.modelGrid.nx1 / 2))
        );
      }

      // this is if nothing worked basically
      return previous?.value ?? 0;
    },
  });
  public readonly crossline = linkedSignal<
    {
      log: WellLog;
      job: JobDetail;
    },
    number
  >({
    source: () => ({ log: this.selectedLog(), job: this.jobData() }),
    computation: (source, previous) => {
      // If this is the FIRST time the signal is calculating (no previous value)
      // AND we have a seed value from the URL, use it.
      if (!previous && "crossline" in this.urlSeed) {
        return Number(this.urlSeed["crossline"]);
      }

      let { job, log } = source;
      // if log is selected, that is the primary case. that inline/crossline used should be that
      if (log) return job.x2_is_inline ? log.inline : log.crossline;

      // do a check here to ensure that the modelGrid is the same
      // if grid is the same, we just preserve the value
      if (
        previous?.value &&
        previous.source.job.modelGrid.nx1 === source.job.modelGrid.nx1 &&
        previous.source.job.modelGrid.nx2 === source.job.modelGrid.nx2 &&
        previous.source.job.modelGrid.dx === source.job.modelGrid.dx
      ) {
        return previous.value;
      }

      // Detect if job change
      const isNewJob = source.job.id !== previous?.source?.job.id;
      if (isNewJob) {
        // get the old settings, to see if there are any. if not, we can use initials, otherwise, use the saved ones.
        let settings = this.settingsService.jobSettings();
        return (
          settings["crossline"] ??
          (job.x2_is_inline
            ? Math.floor(job.modelGrid.nx1 / 2)
            : Math.floor(job.modelGrid.nx2 / 2))
        );
      }

      // If we reach here, it's either a deselection or a UI interaction.
      // Return the current value of the signal to keep it "as is".
      return previous?.value ?? 0;
    },
  });
  public readonly depth = linkedSignal<JobDetail, number>({
    source: this.jobData,
    computation: (source, previous) => {
      if (!previous && "depth" in this.urlSeed) {
        return Number(this.urlSeed["depth"]);
      }

      // do a check here to ensure that the modelGrid is the same
      // if grid is the same, we just preserve the value
      if (
        previous?.value &&
        previous.source.modelGrid.nx3 === source.modelGrid.nx3 &&
        previous.source.modelGrid.dx === source.modelGrid.dx
      ) {
        return previous.value;
      }

      let settings = this.settingsService.jobSettings();
      return (
        settings["depth"] ?? (source ? Math.floor(source.modelGrid.nx3 / 2) : 0)
      );
    },
  });

  public readonly iteration = linkedSignal<JobDetail, number>({
    source: this.jobData,
    computation: (job, previous) => {
      if (!previous && "iteration" in this.urlSeed) {
        return Number(this.urlSeed["iteration"]);
      }
      let settings = this.settingsService.jobSettings();
      let iter = settings["iteration"] ?? job?.iterationsComplete ?? 0;
      return iter;
    },
  });

  selectedLog = signal<WellLog>(null);
  markersVisibility = signal<{ [key: string]: boolean }>({});
  selectedHorizon = signal<GeneralizedHorizon[]>([]);
  logFrequency = signal<number>(0);
  blockRef = signal<boolean>(false);
  AGCWindow = linkedSignal<number, number>({
    source: () => this.jobData().modelGrid.nx3,
    computation: (nx3, previous) => nx3 / 10,
  });

  // per-axis dx overrides. null = variable dx is off and every axis uses the modelGrid dx
  dxOverride = signal<{ dx1: number; dx2: number; dx3: number } | null>(null);
  variableDx = computed(() => this.dxOverride() !== null);
  dx1 = computed(() => this.dxOverride()?.dx1 ?? this.jobData()!.modelGrid.dx);
  dx2 = computed(() => this.dxOverride()?.dx2 ?? this.jobData()!.modelGrid.dx);
  dx3 = computed(() => this.dxOverride()?.dx3 ?? this.jobData()!.modelGrid.dx);

  // a dx change changes the model extent in metres, so the ranges are reset to match.
  // turning variable dx on starts every override at the default, so nothing changes until a dx is edited
  setVariableDx(on: boolean) {
    const dx = this.jobData()!.modelGrid.dx;
    this.dxOverride.set(on ? { dx1: dx, dx2: dx, dx3: dx } : null);
    if (!on) this.resetRanges();
  }

  setDx(axis: "dx1" | "dx2" | "dx3", value: number) {
    this.dxOverride.update((override) => override && { ...override, [axis]: value });
    this.resetRanges();
  }

  private restoreSavedView(job: JobDetail) {
    const saved = this.settingsService.get(job.id);
    this.dxOverride.set(saved["dxOverride"] ?? null);

    if (saved["rangesEnd"] && !this.preserveRanges()) {
      for (const axis of ["IL", "XL", "depth"] as const) {
        this.rangesStart[axis].set(saved["rangesStart"][axis]);
        this.rangesEnd[axis].set(saved["rangesEnd"][axis]);
      }
    }
  }

  // depth is always dx3. inline/crossline are dx1/dx2, swapped when x2_is_inline
  inlineDx = computed(() =>
    this.jobData()?.x2_is_inline ? this.dx2() : this.dx1(),
  );
  crosslineDx = computed(() =>
    this.jobData()?.x2_is_inline ? this.dx1() : this.dx2(),
  );

  vpRef = linkedSignal<
    { iteration: number; blockRef: boolean; model: string; job: JobDetail },
    number
  >({
    source: () => ({
      iteration: this.iteration(),
      blockRef: this.blockRef(),
      model: this.selectedModel(),
      job: this.jobData(), // this should also run on job change. on job change, we should probably set it to 0
    }),
    computation: (source, previous) => {
      // must be an update model. otherwise stop
      if (!source.model.toLowerCase().includes("update")) {
        return null;
      }
      // if sync is ON, return the previous value
      if (this.synchroniseSettings()) {
        return previous?.value || 0;
      }

      if (source.blockRef) {
        // source blockRef true? return the relevant vpRef iterations
        return this.getStartOfBlockIteration(source.iteration);
      }

      // if there is a job change, we should set it to 0 OR the previously available value
      if (previous && source.job.id !== previous.source.job.id) {
        return (
          this.modelViewerSettings.savedModelSettings?.[source.job.id]?.[
            source.model
          ]?.["vpRef"] ?? 0
        );
      }

      return previous?.value || 0;
    },
  });

  getStartOfBlockIteration(iteration: number) {
    // if on iteration 0, just leave
    if (iteration === 0) {
      return 0;
    }
    let data = this.functionalTableResource.value();
    let iters = data?.map((d) => d.iter);
    // progressive sum array
    let sum = 0;
    const iters_sum = iters?.map((v) => {
      sum += v;
      return sum;
    });

    if (iteration - 1 <= iters_sum[0]) {
      // this means that its either the first block, or the first iteration of the 1st block
      // bioth cases should set VP as 0
      return 0;
    }

    let block_index = iters_sum.findIndex((v) => v >= iteration - 1);
    let start_of_block = iters_sum[block_index - 1] + 1;
    return start_of_block;
  }

  selectedModel = linkedSignal<{ job: JobDetail }, string>({
    source: () => ({
      job: this.jobData(),
    }),
    computation: (source, previous) => {
      if (!previous && "model" in this.urlSeed) {
        return this.urlSeed["model"];
      }

      // this does the syncSettings magic
      let syncSettings = untracked(() => this.synchroniseSettings());
      if (syncSettings && previous?.value) {
        return previous?.value;
      }
      // if sync settings not, we do wanna see the cached settings for the previously active model
      // the type checking around here is an 'edge' case. previously we used to store objects, as such alot of the users had old saved objects instead of the new strings
      // to prevent that error, this is applied. this can hence be removed pretty much whenever now considering
      // by the time you're looking at this its been a while
      var cachedModel =
        this.modelViewerSettings.savedModelSettings[source.job.id]?.activeModel;
      return (
        (typeof cachedModel === "string" ? cachedModel : null) ?? DEFAULT_MODEL
      );
    },
  });
  // this just preserves the state of the previously selected model
  previouslySelectedModel = linkedSignal<string, string>({
    source: () => this.selectedModel(),
    computation: (_, previous) => previous?.source,
  });
  selectedShot = signal<number>(null);

  selectedCmap = linkedSignal<{ model: string; job: JobDetail }, string>({
    source: () => ({ model: this.selectedModel(), job: this.jobData() }),
    computation: (source, previous) => {
      let syncSettings = untracked(() => this.synchroniseSettings());
      // sync settings supersedes everything
      // but it should only work if the job is different and model is the same
      if (syncSettings && previous) {
        // same model, and different job
        if (
          previous.source.model === source.model &&
          previous.source.job.id !== source.job.id
        ) {
          return previous.value;
        }
      }

      // figure the cacheing here. try catch for the nested dict existence check
      try {
        let cachedCmap =
          this.modelViewerSettings.savedModelSettings[source.job.id][
            source.model
          ].cmap;
        if (cachedCmap) return cachedCmap;
      } catch (err) {}

      // these are all if there is NONE in the cache and no sync
      if (source.model.toLowerCase().includes("update")) {
        return "red_white_blue";
      }
      if (
        allSliceProcessings[source.model]?.includes(
          "_zero_centred_params_process",
        )
      ) {
        return "greyscale";
      }
      return "SEGRefl2";
    },
  });

  // --- RESOURCE ---
  public readonly functionalTableResource = rxResource({
    params: () => ({
      id: this.jobData()?.id,
    }),
    stream: ({ params }) => {
      if (!params.id) return EMPTY;
      return this.cachedProjectService.getFunctionalSequence(params.id).pipe(
        switchMap((res) => {
          if (res) {
            let data = res.data;
            let dataSource = [];
            for (let i in data) {
              let datum = data[i];
              let additionals_string = "";
              if (Object.keys(datum["additionals"]).length > 0) {
                additionals_string =
                  "(" +
                  Object.entries(datum["additionals"])
                    .map(([key, value]) => `${key}: ${value}`)
                    .join(", ") +
                  ")";
              }

              // the additional string is added to iteratios or as additionals (which are added beside functional or iteration) depending on the functional
              // logic for WHERE the additional string is added is in the template
              let entry = {
                block: Number(i) + 1,
                block_started:
                  datum.iterations[0] <= this.jobData().iterationsComplete,
                ...datum,
                additionals: additionals_string,
                iterations: datum["iterations"].join(" - "),
              };
              dataSource.push(entry);
            }
            dataSource = Array.from(dataSource);
            return of(dataSource);
          }
        }),
        catchError((err) => {
          throw new Error(err.error?.error);
        }),
      );
    },
  });

  public readonly availableModelsResource = rxResource({
    params: () => {
      const job = this.jobData();
      // If we don't have a job yet, don't even access this.iteration(), cause otherwise it breaks the dependencies honestly
      if (!job?.id) return null;
      return {
        jobId: job.id,
        iteration: this.iteration(),
      };
    },
    stream: ({ params }) => {
      if (!params.jobId) return EMPTY;
      return this.cachedProjectService
        .getModelTypes(params.jobId, params.iteration)
        .pipe(map((modelTypes) => modelTypes.map((modelType) => modelType.id)));
    },
  });

  public readonly shotLocationsResource = rxResource({
    params: () => ({
      id: this.jobData()?.id,
      iterations: this.jobData()?.iterations,
    }),
    stream: ({ params }) => {
      if (!params.id) return EMPTY;

      return this.cachedProjectService
        .getCosts(params.id, params.iterations)
        .pipe(
          map((data) => {
            // this simply converts the array of 'costItems' which have the shot sx, sy, sz, to a dict so we can do O(1) lookups later
            return (
              data?.costItems.reduce((acc, shot) => {
                acc[shot.shotNo] = { sx: shot.sx, sy: shot.sy, sz: shot.sz };
                return acc;
              }, {}) || {}
            );
          }),
          catchError((err) => {
            this.nzNotificationService.error(
              "Error fetching show locations",
              err.error?.error ||
                err?.message ||
                "An unexpected error occurred",
            );
            return of({});
          }),
        );
    },
  });

  public readonly horizonsResource = rxResource<
    {
      jobHorizons: G_JobHorizon[];
      projectHorizons: G_ProjectHorizon[];
    },
    { id: string }
  >({
    params: () => ({
      id: this.jobData()?.id,
    }),
    stream: ({ params }) => {
      if (!params.id) return EMPTY;
      return forkJoin([
        this.cachedProjectService.getHorizonTypes(params.id).pipe(take(1)),
        this.cachedProjectService.currentProject.pipe(
          take(1),
          map((p) => p?.horizons),
          catchError((err) => {
            console.error("Error extracting horizons:", err);
            return of([]);
          }),
        ),
      ]).pipe(
        map(([jobHorizons, projectHorizons]) => {
          return { jobHorizons: jobHorizons, projectHorizons: projectHorizons };
        }),
        catchError((err) => {
          console.error("Critical Horizons merge failure:", err);
          return of({ jobHorizons: [], projectHorizons: [] });
        }),
      );
    },
  });

  public readonly colorMapResource = rxResource({
    stream: () => {
      return this.cachedPaletteService.getPalettes();
    },
  });

  constructor() {
    // runs and updated the URL params after the job has been initialized and all things are initialized
    effect(() => {
      const job = this.jobData();
      if (!job?.id) return; // The "Guard"

      // Capture values to track them
      const params = {
        iteration: this.iteration(),
        depth: this.depth(),
        inline: this.inline(),
        crossline: this.crossline(),
        model: this.selectedModel(),
      };

      // Manual Debounce
      const timeout = setTimeout(() => {
        untracked(() => {
          this.router.navigate([], {
            queryParams: params,
            relativeTo: this.route,
            queryParamsHandling: "merge",
            replaceUrl: true,
          });
        });
      }, 500);

      // Effect cleanup function
      return () => clearTimeout(timeout);
    });
  }

  public resetRanges(job?: JobDetail) {
    var data = null;
    if (!job) data = this.jobData();
    else data = job;
    this.rangesStart.XL.set(0);
    this.rangesStart.IL.set(0);
    this.rangesStart.depth.set(0);

    const inlineDx = job ? job.modelGrid.dx : this.inlineDx();
    const crosslineDx = job ? job.modelGrid.dx : this.crosslineDx();
    const depthDx = job ? job.modelGrid.dx : this.dx3();

    this.rangesEnd.IL.set(
      (data.x2_is_inline ? data.modelGrid.nx2 : data.modelGrid.nx1) * inlineDx,
    );
    this.rangesEnd.XL.set(
      (data.x2_is_inline ? data.modelGrid.nx1 : data.modelGrid.nx2) *
        crosslineDx,
    );
    this.rangesEnd.depth.set(data.modelGrid.nx3 * depthDx);
  }

  async refreshJob() {
    await firstValueFrom(
      this.cachedProjectService.getJobDetail(this.jobData().id, true),
    );
  }

  /**
   * Reparse the given job. The job is passed in (rather than read from jobData())
   * so switching jobs mid-request still reparses the job that asked for it.
   */
  public reparseJob(job: JobDetail) {
    if (!job || this.reparsingJobIds.has(job.id)) return;
    this.reparsingJobIds.add(job.id);

    const loading = this.nzMessageService.loading(
      `Updating ${job.name} with new iterations…`,
      { nzDuration: 0 },
    ).messageId;

    // clear cache for job + project
    this.cachedProjectService.clearProjectsCache();
    this.cachedProjectService.clearJobDetailCache([job.id]);
    // construct entries and reparse job
    let entries = [
      {
        path: `/${job.basePath}`,
        parent_job_id: job.parent_job_id,
        parent_cp_num: job.parent_cp_num,
        x2_is_inline: job.x2_is_inline,
        is_update: true,
      },
    ];
    this.cachedProjectService
      .createJobs(job.projectId, entries)
      .pipe(
        take(1),
        finalize(() => {
          this.reparsingJobIds.delete(job.id);
          this.nzMessageService.remove(loading);
        }),
      )
      .subscribe({
        next: (res) => {
          // failed | error messages
          res["failed"].forEach((element) => {
            this.nzNotificationService.error(
              "Error",
              `Job with path: <div>${element["path"]} </div> <br> failed to be re-parsed: <br><b>${element["error"]}</b>`,
            );
          });

          // warning messages
          res["passed"].forEach((element) => {
            let warnings = element["warnings"] || [];
            warnings.forEach((warning: string) => {
              this.nzNotificationService.warning("Warning", warning);
            });
          });

          if (res["passed"].length > 0) {
            this.nzMessageService.success(`${job.name} updated with new iterations`);
            // only refresh if the user is still on this job; getJobDetail sets the current job
            // and would otherwise pull them back. The cache is already cleared for the other case.
            if (this.jobData()?.id === job.id) this.refreshJob();
          }
        },
        error: (err) => {
          this.nzNotificationService.error(
            "Error",
            err.error?.error || "An unexpected error occured",
          );
        },
      });
  }
}
